import { useState, useEffect, useCallback } from 'react'
import toast from 'react-hot-toast'
import { api } from '../../services/api'
import { Bell, Send, MessageSquare, ChevronDown, ChevronUp, CheckCircle, UserPlus } from 'lucide-react'

export default function TrainerNotesPage() {
  const [notes, setNotes] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [expanded, setExpanded] = useState<string | null>(null)
  const [threads, setThreads] = useState<Record<string, any[]>>({})
  const [replyText, setReplyText] = useState<Record<string, string>>({})
  const [sending, setSending] = useState<string | null>(null)
  const [trainerInfo, setTrainerInfo] = useState<{ linked: boolean; trainer: any }>({ linked: false, trainer: null })
  const [compose, setCompose] = useState('')
  const [sendingNew, setSendingNew] = useState(false)
  const [linkCode, setLinkCode] = useState('')
  const [linking, setLinking] = useState(false)

  const loadNotes = useCallback(async () => {
    setLoading(true)
    let trainerRes: { linked: boolean; trainer: any } = { linked: false, trainer: null }
    try {
      const tr = await api.get('/trainer/notes/my-trainer')
      trainerRes = {
        linked: Boolean(tr.data?.linked),
        trainer: tr.data?.trainer || null,
      }
    } catch {
      /* my-trainer opcional */
    }
    setTrainerInfo(trainerRes)

    try {
      const notesRes = await api.get('/trainer/notes/my')
      const list = Array.isArray(notesRes.data) ? notesRes.data : []
      const rootNotes = list.filter((n: any) => !n.parentId)
      setNotes(rootNotes)
      await api.post('/trainer/notes/read').catch(() => {})
    } catch {
      setNotes([])
      if (trainerRes.linked) {
        toast.error('No se pudieron cargar las notas')
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadNotes()
  }, [loadNotes])

  const loadThread = async (noteId: string) => {
    if (expanded === noteId) { setExpanded(null); return }
    setExpanded(noteId)
    try {
      const { data } = await api.get(`/trainer/notes/${noteId}/thread`)
      setThreads((t) => ({ ...t, [noteId]: data }))
    } catch {
      toast.error('No se pudo abrir el hilo')
    }
  }

  const sendReply = async (noteId: string) => {
    const text = replyText[noteId]?.trim()
    if (!text) return
    setSending(noteId)
    try {
      await api.post(`/trainer/notes/${noteId}/reply`, { message: text })
      setReplyText((r) => ({ ...r, [noteId]: '' }))
      const { data } = await api.get(`/trainer/notes/${noteId}/thread`)
      setThreads((t) => ({ ...t, [noteId]: data }))
      toast.success('Respuesta enviada')
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'No se pudo enviar la respuesta')
    } finally {
      setSending(null)
    }
  }

  const sendNew = async () => {
    const text = compose.trim()
    if (!text) return
    setSendingNew(true)
    try {
      await api.post('/trainer/notes/to-trainer', { message: text })
      setCompose('')
      toast.success('Mensaje enviado a tu entrenador')
      await loadNotes()
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'No se pudo enviar el mensaje')
    } finally {
      setSendingNew(false)
    }
  }

  const linkByCode = async () => {
    const code = linkCode.trim()
    if (!code) return
    setLinking(true)
    try {
      await api.post('/trainer/link-by-code', { trainerCode: code })
      toast.success('Entrenador vinculado')
      setLinkCode('')
      await loadNotes()
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Código inválido o entrenador no encontrado')
    } finally {
      setLinking(false)
    }
  }

  const formatDate = (d: string) => new Date(d).toLocaleDateString('es-CO', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-400">Cargando notas...</div>
    )
  }

  const trainerName = trainerInfo.trainer
    ? `${trainerInfo.trainer.firstName || ''} ${trainerInfo.trainer.lastName || ''}`.trim()
    : ''

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center">
          <Bell size={20} className="text-white" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-800">Notas de tu Entrenador</h1>
          <p className="text-sm text-gray-500">
            {notes.length} mensaje(s)
            {trainerInfo.linked && trainerName ? ` · Coach: ${trainerName}` : ''}
          </p>
        </div>
      </div>

      {!trainerInfo.linked ? (
        <div className="bg-white rounded-2xl border border-amber-200 p-5 mb-6 space-y-4">
          <div className="flex items-start gap-3">
            <UserPlus className="text-amber-600 flex-shrink-0 mt-0.5" size={22} />
            <div>
              <p className="font-semibold text-gray-800">Vincula un entrenador para enviar y recibir notas</p>
              <p className="text-sm text-gray-500 mt-1">
                En Food Plan el chat de notas requiere un coach asignado. Usa el código que te dio tu entrenador.
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              className="input flex-1 text-sm"
              placeholder="Código del entrenador (ej. TRN-XXXXX)"
              value={linkCode}
              onChange={(e) => setLinkCode(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === 'Enter' && linkByCode()}
            />
            <button
              type="button"
              onClick={linkByCode}
              disabled={linking || !linkCode.trim()}
              className="btn-primary disabled:opacity-40"
            >
              {linking ? 'Vinculando…' : 'Vincular'}
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 mb-6">
          <p className="text-sm font-semibold text-gray-800 mb-2">Escribir a {trainerName || 'tu entrenador'}</p>
          <div className="flex gap-2">
            <textarea
              className="input flex-1 text-sm min-h-[72px] resize-y"
              placeholder="Escribe una nota para tu entrenador…"
              value={compose}
              onChange={(e) => setCompose(e.target.value)}
            />
            <button
              type="button"
              onClick={sendNew}
              disabled={sendingNew || !compose.trim()}
              className="self-end p-2.5 bg-primary-600 text-white rounded-xl hover:bg-primary-700 disabled:opacity-40 transition-colors"
              aria-label="Enviar nota"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}

      {notes.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <Bell size={40} className="mx-auto mb-3 opacity-30" />
          <p>
            {trainerInfo.linked
              ? 'Aún no hay mensajes. Escribe arriba para iniciar la conversación.'
              : 'Cuando tengas entrenador, aquí verás sus notas y podrás responder.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {notes.map((note) => {
            const fromUser = note.author === 'USER'
            return (
              <div key={note.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="p-4">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        fromUser ? 'bg-primary-100' : 'bg-amber-100'
                      }`}>
                        <span className={`text-xs font-bold ${fromUser ? 'text-primary-700' : 'text-amber-700'}`}>
                          {fromUser
                            ? 'Tú'
                            : `${note.trainer?.firstName?.[0] || ''}${note.trainer?.lastName?.[0] || 'E'}`}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-800">
                          {fromUser
                            ? 'Tú → entrenador'
                            : `${note.trainer?.firstName || ''} ${note.trainer?.lastName || 'Entrenador'}`.trim()}
                        </p>
                        <p className="text-xs text-gray-400">{formatDate(note.createdAt)}</p>
                      </div>
                    </div>
                    {note.isRead && !fromUser && (
                      <span className="text-xs text-green-600 flex items-center gap-1 flex-shrink-0">
                        <CheckCircle size={12} /> Leída
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{note.message}</p>
                </div>

                <button
                  type="button"
                  onClick={() => loadThread(note.id)}
                  className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 border-t border-gray-100 text-sm text-gray-500 hover:bg-gray-100 transition-colors"
                >
                  <span className="flex items-center gap-2">
                    <MessageSquare size={14} />
                    {threads[note.id]?.length > 1
                      ? `${threads[note.id].length - 1} respuesta(s)`
                      : 'Responder / ver hilo'}
                  </span>
                  {expanded === note.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>

                {expanded === note.id && (
                  <div className="border-t border-gray-100">
                    {threads[note.id]?.filter((m: any) => m.id !== note.id).map((msg: any) => (
                      <div
                        key={msg.id}
                        className={`p-3 border-b border-gray-50 ${
                          msg.author === 'USER' ? 'bg-primary-50' : 'bg-amber-50'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs font-semibold ${
                            msg.author === 'USER' ? 'text-primary-700' : 'text-amber-700'
                          }`}>
                            {msg.author === 'USER' ? '👤 Tú' : `🏋️ ${msg.trainer?.firstName || 'Entrenador'}`}
                          </span>
                          <span className="text-xs text-gray-400">{formatDate(msg.createdAt)}</span>
                        </div>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{msg.message}</p>
                      </div>
                    ))}

                    {trainerInfo.linked && (
                      <div className="p-3 flex gap-2">
                        <input
                          className="input flex-1 text-sm"
                          placeholder="Escribe tu respuesta..."
                          value={replyText[note.id] || ''}
                          onChange={(e) => setReplyText((r) => ({ ...r, [note.id]: e.target.value }))}
                          onKeyDown={(e) => e.key === 'Enter' && sendReply(note.id)}
                        />
                        <button
                          type="button"
                          onClick={() => sendReply(note.id)}
                          disabled={sending === note.id || !replyText[note.id]?.trim()}
                          className="p-2.5 bg-primary-600 text-white rounded-xl hover:bg-primary-700 disabled:opacity-40 transition-colors"
                        >
                          <Send size={15} />
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
